import UploadIcon from '@src/svgs/quick-actions/UploadIcon';
import { forwardRef } from 'react';
import styled from 'styled-components';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** "Upload Photo", "Upload Video", "Upload Audio": the verb and the noun. */
  children: ReactNode;
};

/**
 * The upload action at the head of a side panel's media list: Secondary tier,
 * `sm`, full width, with the upload arrow as its 16px leading icon
 * (docs/design-system.md § "Upload buttons in side panels").
 *
 * The Media panel's three tabs each hand-rolled a full-width solid-green `lg`
 * block with a different glyph (a long arrow, a photo, a cloud). In a panel the
 * media is the content, and a 40px green slab over it was the loudest thing on
 * screen (PLAT-12136).
 *
 * Hosts open their file picker from `onClick`, keeping the
 * `<input type="file">` a hidden sibling (`common/FileUpload` does this).
 */
const PanelUploadButton = forwardRef<HTMLButtonElement, Props>(
  ({ children, type = 'button', ...rest }, ref) => (
    <Button ref={ref} type={type} {...rest}>
      <UploadIcon width="16" height="16" />
      {children}
    </Button>
  ),
);

PanelUploadButton.displayName = 'PanelUploadButton';

export default PanelUploadButton;

const Button = styled.button`
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  background: transparent;
  color: var(--foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);

  svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
